/**
 * FRONSYSTEM 4.2 — единая поверхность ошибок для shell (Layout):
 * баннеры, blocking, модалка компании, согласия.
 * Используется основным `utils/axios.js` и вторичными клиентами (например `analyticsV1`).
 */
import {
  pickSafeRussianBackendMessage,
  pickHttpStatusRussianFallback,
} from './safeRussianBackendMessage';

const dispatchWindowEvent = (name, detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const normalizeAgreementDocuments = (resData, errorCode, rawMessage) => {
  const docs = Array.isArray(resData.documents)
    ? resData.documents
    : Array.isArray(resData.agreements_required)
      ? resData.agreements_required
      : [];

  if (docs.length > 0) {
    return docs.map((doc, index) => ({
      id: doc.id || doc.consent_type || doc.document_id || `${errorCode || 'agreement'}-${index}`,
      consent_type: doc.consent_type || doc.agreement_code || doc.id || null,
      audience: doc.audience || 'user',
      title: doc.title || 'Необходимое соглашение',
      content: doc.content || doc.summary || rawMessage || 'Для продолжения необходимо принять соглашение.',
      version: doc.version || '1.0',
      basis: doc.basis || doc.legal_basis || 'Требование системы',
      summary: doc.summary || null,
      document_id: doc.document_id || null,
      module_scope: doc.module_scope || null,
    }));
  }

  return [
    {
      id: errorCode || 'agreement',
      consent_type: errorCode || null,
      audience: 'user',
      title: errorCode === 'AI_CONSENT_REQUIRED'
        ? 'Согласие на использование AI и обработку данных'
        : 'Необходимое соглашение',
      content: rawMessage || 'Для продолжения необходимо принять соглашение.',
      version: resData.version || '1.0',
      basis: 'Требование системы',
    },
  ];
};

export const buildCompanyBlockPayload = (error) => {
  const d = error.response?.data || {};
  const inner = d.data && typeof d.data === 'object' ? d.data : {};
  return {
    ...inner,
    message: d.message || d.error || null,
  };
};

export const ACCESS_BANNER_RATE_CODES = new Set([
  'LIMIT_EXCEEDED',
  'DAILY_TOKEN_LIMIT',
  'MONTHLY_LIMIT',
  'RATE_LIMIT',
  'RATE_LIMIT_EXCEEDED',
  'DAILY_LIMIT_EXCEEDED',
  'GLOBAL_RATE_LIMIT_EXCEEDED',
  'GLOBAL_BURST_LIMIT_EXCEEDED',
]);

export const resolveAccessBannerUrl = (errorCode, requestUrl) => {
  const u = String(requestUrl || '');
  if (u.includes('/api/assistant')) return '/settings/assistant';
  if (errorCode === 'INSUFFICIENT_BALANCE') return '/settings/balance';
  if (errorCode === 'SECURITY_UNAVAILABLE') return '/contact';
  return '/tariffs';
};

const ERROR_CODES_MAP = {
  ERR_VALIDATION: 'Проверьте, пожалуйста, правильность заполнения полей.',
  E_AUTH_FAILED: 'Неверный логин или пароль. Попробуйте ещё раз.',
  E_NOT_FOUND: 'Запрашиваемая запись не найдена.',
  E_DUPLICATE: 'Такая запись уже существует в системе.',
  E_RATE_LIMIT: 'Слишком много запросов. Пожалуйста, подождите немного.',
  E_SERVER_ERROR: 'Внутренняя ошибка сервера. Попробуйте повторить действие чуть позже.',
  E_FORBIDDEN: 'У вас недостаточно прав для выполнения этого действия.',

  // Компания
  COMPANY_REQUIRED: 'Для этого действия нужна активная компания. Создайте компанию или попросите владельца пригласить вас через HRM (команда).',
  COMPANY_INACTIVE: 'Компания временно заблокирована. Обратитесь в поддержку платформы.',
  ADMIN_DEACTIVATED: 'Компания отключена администратором платформы. Обратитесь в поддержку.',
  COMPANY_CREATION_DISABLED: 'Создание новых компаний временно отключено на платформе.',

  // Верификация и согласия
  VERIFICATION_REQUIRED: 'Для этого действия компания должна пройти верификацию. Откройте настройки связанных компаний.',
  COMPANY_NOT_VERIFIED: 'Компания ещё не верифицирована. Загрузите документы в настройках.',
  AGREEMENT_REQUIRED: 'Необходимо принять актуальное соглашение. Откройте раздел «Документы и согласия».',
  AGREEMENTS_REQUIRED: 'Необходимо принять актуальные соглашения. Откройте раздел «Документы и согласия».',
  CONSENT_REQUIRED: 'Для продолжения нужно подтвердить согласие.',
  AI_CONSENT_REQUIRED: 'Для работы с AI-функциями нужно принять согласие на использование AI и обработку данных.',
  FEED_AGREEMENT_REQUIRED: 'Для ленты/уведомлений нужно принять отдельное согласие.',

  // Пользователь и сессия
  USER_INACTIVE: 'Аккаунт временно деактивирован. Обратитесь в поддержку или к администратору компании.',
  TOKEN_EXPIRED: 'Время сессии истекло. Пожалуйста, войдите заново.',
  SESSION_EXPIRED: 'Время сессии истекло. Пожалуйста, войдите заново.',

  // Модули, тарифы, баланс
  FEATURE_NOT_AVAILABLE: 'Функция недоступна на текущем тарифе или отключена администратором.',
  MODULE_DISABLED: 'Модуль отключён для компании или временно недоступен. Проверьте тариф и настройки.',
  INSUFFICIENT_BALANCE: 'Недостаточно средств на балансе. Пополните кошелёк компании.',
  LIMIT_EXCEEDED: 'Достигнут лимит тарифа. Обновите тариф или повторите позже.',
  DAILY_LIMIT_EXCEEDED: 'Превышен дневной лимит. Повторите действие завтра или обновите тариф.',
  MONTHLY_LIMIT: 'Превышен месячный лимит. Обновите тариф для расширения лимита.',
  RATE_LIMIT: 'Слишком много запросов подряд. Пожалуйста, немного подождите.',
  RATE_LIMIT_EXCEEDED: 'Слишком много запросов подряд. Пожалуйста, немного подождите.',

  // Безопасность
  SECURITY_UNAVAILABLE: 'Контур безопасности сейчас недоступен. Попробуйте позже или обратитесь в поддержку.',
  CAPTCHA_REQUIRED: 'Для продолжения нужно пройти проверку «я не робот».',
  CAPTCHA_FAILED: 'Проверка «я не робот» не пройдена. Попробуйте ещё раз.',
  CAPTCHA_UNAVAILABLE: 'Проверка безопасности временно недоступна. Попробуйте снова через 30 секунд.',
};

/**
 * Текст для toast / локального reject после ответа API.
 */
export function getApiErrorToastBody(error) {
  if (!error?.response) {
    return pickSafeRussianBackendMessage(null, 'Произошла ошибка');
  }
  const status = error.response.status;
  const resData = error.response.data || {};
  const errorCode = resData.code || resData.error_code;
  const rawMessage = resData.message || resData.error;
  const mappedCode = errorCode && ERROR_CODES_MAP[errorCode];
  const errorMessage = mappedCode || rawMessage || null;
  return pickSafeRussianBackendMessage(
    typeof errorMessage === 'string' ? errorMessage : null,
    pickHttpStatusRussianFallback(status)
  );
}

const mergeBlockingData = (resData) => {
  const inner = resData.data && typeof resData.data === 'object' ? { ...resData.data } : {};
  const m = resData.message || resData.error;
  if (m) inner.message = m;
  return Object.keys(inner).length ? inner : null;
};

/**
 * Диспатчит события shell при известных кодах/статусах.
 * @returns {boolean} true — сценарий обработан (основной axios не дублирует toast для этих кейсов).
 */
export function trySurfaceShellHttpError(error, { requestUrl = '' } = {}) {
  if (!error?.response) return false;

  const status = error.response.status;
  const resData = error.response.data || {};
  const errorCodeRaw = resData.code || resData.error_code || resData.error;
  const errorCode = typeof errorCodeRaw === 'string' ? errorCodeRaw.toUpperCase() : errorCodeRaw;
  const rawMessage = resData.message || resData.error;
  const toastBody = getApiErrorToastBody(error);

  if (
    (status === 402 && errorCode === 'INSUFFICIENT_BALANCE')
    || (status === 400 && errorCode === 'INSUFFICIENT_BALANCE')
    || (status === 429 && ACCESS_BANNER_RATE_CODES.has(errorCode))
  ) {
    dispatchWindowEvent('app_access_banner', {
      variant: status === 429 ? 'warning' : 'error',
      message: toastBody,
      code: errorCode,
      actionUrl: resolveAccessBannerUrl(errorCode, requestUrl),
    });
    return true;
  }

  if (status === 403 && errorCode === 'COMPANY_REQUIRED') {
    // Already on company create/join — do not cover the wizard with a full-screen 403 (hang UX).
    const path = typeof window !== 'undefined' ? String(window.location.pathname || '') : '';
    if (
      path === '/company/create'
      || path === '/company/join'
      || path === '/settings/connected-companies'
    ) {
      return true;
    }
    dispatchWindowEvent('app_blocking_state', {
      type: 'COMPANY_REQUIRED',
      data: mergeBlockingData(resData),
      action: resData.action || null,
      source: 'http-client',
    });
    return true;
  }

  if (status === 403 && (errorCode === 'COMPANY_NOT_VERIFIED' || errorCode === 'VERIFICATION_REQUIRED')) {
    dispatchWindowEvent('app_blocking_state', {
      type: 'VERIFICATION_REQUIRED',
      data: mergeBlockingData(resData),
      action: resData.action || null,
      source: 'http-client',
    });
    return true;
  }

  if ((status === 403 || status === 402) && errorCode === 'MODULE_DISABLED') {
    dispatchWindowEvent('app_blocking_state', {
      type: 'MODULE_DISABLED',
      data: mergeBlockingData(resData),
      action: resData.action || { url: '/tariffs' },
      source: 'http-client',
    });
    return true;
  }

  if (status === 403 && errorCode === 'FEATURE_NOT_AVAILABLE') {
    dispatchWindowEvent('app_blocking_state', {
      type: 'FEATURE_NOT_AVAILABLE',
      data: mergeBlockingData(resData),
      action: resData.action || { url: '/tariffs' },
      source: 'http-client',
    });
    return true;
  }

  if (status === 403 && errorCode === 'COMPANY_CREATION_DISABLED') {
    dispatchWindowEvent('app_blocking_state', {
      type: 'COMPANY_CREATION_DISABLED',
      data: mergeBlockingData(resData),
      action: resData.action || { url: '/contact' },
      source: 'http-client',
    });
    return true;
  }

  if (status === 503 && errorCode === 'SECURITY_UNAVAILABLE') {
    // Soft banner — full-screen BlockingState froze cabin (reg→company→data) when CORE security
    // or agreements probe returned 503; user could not dismiss and continue managing data.
    dispatchWindowEvent('app_access_banner', {
      variant: 'warning',
      message: toastBody || ERROR_CODES_MAP.SECURITY_UNAVAILABLE,
      code: errorCode,
      actionUrl: resolveAccessBannerUrl(errorCode, requestUrl) || '/contact',
    });
    return true;
  }

  if (status === 403 && errorCode === 'COMPANY_INACTIVE') {
    if (typeof window !== 'undefined') {
      const payload = buildCompanyBlockPayload(error);
      window.sessionStorage.setItem('company_block_info', JSON.stringify({
        type: errorCode,
        data: payload,
        timestamp: Date.now(),
      }));
      window.dispatchEvent(new CustomEvent('company_blocked', {
        detail: { type: errorCode, data: payload },
      }));
    }
    return true;
  }

  if (status === 403 && errorCode === 'ADMIN_DEACTIVATED') {
    if (typeof window !== 'undefined') {
      const payload = buildCompanyBlockPayload(error);
      window.sessionStorage.setItem('company_block_info', JSON.stringify({
        type: errorCode,
        data: payload,
        timestamp: Date.now(),
      }));
      window.dispatchEvent(new CustomEvent('company_blocked', {
        detail: { type: errorCode, data: payload },
      }));
    }
    return true;
  }

  if (
    [403, 451].includes(status)
    && ['AGREEMENT_REQUIRED', 'AGREEMENTS_REQUIRED', 'CONSENT_REQUIRED', 'AI_CONSENT_REQUIRED', 'FEED_AGREEMENT_REQUIRED'].includes(errorCode)
  ) {
    dispatchWindowEvent('app_agreement_required', {
      code: errorCode === 'AGREEMENTS_REQUIRED' ? 'AGREEMENT_REQUIRED' : errorCode,
      consentUrl: resData.consent_url || null,
      action: resData.action || null,
      message: resData.error || rawMessage || 'Необходимо принять соглашение.',
      documents: normalizeAgreementDocuments(resData, errorCode, rawMessage),
    });
    return true;
  }

  return false;
}
